import axios from 'axios';

// Configuración base de axios.
// Puerto backend unificado: 5000
// initApi intentará primero este puerto y luego otros alternativos
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Puertos alternativos en caso de fallback (mantener orden lógico)
const FALLBACK_PORTS = [5000, 5001, 50001, 3001, 50002, 3000, 8080];

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Aumentamos timeout a 30 segundos para evitar falsos timeouts en red local
});

// Interceptor para agregar el token de autenticación
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Log útil para depuración: mostrar la URL final que axios intentará llamar
    try {
      const base = config.baseURL || api.defaults.baseURL || API_BASE_URL;
      console.log(`➡️ Petición API -> ${config.method?.toUpperCase() || 'GET'} ${base}${config.url}`);
    } catch (e) {
      console.log('➡️ Petición API -> (no se pudo mostrar URL)', e?.message || e);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Función para probar la conectividad del backend y ajustar baseURL automáticamente
export const initApi = async () => {
  // Si estamos en producción (no localhost), usar directamente el API_BASE_URL configurado
  const isProduction = !window.location.hostname.includes('localhost') && 
                       !window.location.hostname.includes('127.0.0.1');
  
  if (isProduction) {
    // En producción, usar siempre /api (proxy de Apache)
    const productionUrl = '/api';
    api.defaults.baseURL = productionUrl;
    console.log('✅ Modo producción - usando proxy Apache:', productionUrl);
    return { success: true, baseURL: productionUrl };
  }

  // En desarrollo, intentar primero con API_BASE_URL
  try {
    console.log(`🔍 Probando API_BASE_URL: ${API_BASE_URL}`);
    // API_BASE_URL ya incluye /api, solo agregamos /test
    const testUrl = API_BASE_URL.endsWith('/api') ? `${API_BASE_URL}/test` : `${API_BASE_URL}/api/test`;
    const response = await axios.get(testUrl, { timeout: 5000 });
    if (response.status === 200) {
      // Si API_BASE_URL no termina en /api, lo agregamos
      const baseUrl = API_BASE_URL.endsWith('/api') ? API_BASE_URL : `${API_BASE_URL}/api`;
      api.defaults.baseURL = baseUrl;
      console.log('✅ Backend conectado correctamente en:', baseUrl);
      return { success: true, baseURL: baseUrl };
    }
  } catch (e) {
    console.warn('🔍 No se pudo conectar al backend en el puerto principal:', e.message);
  }

  // Probar puertos alternativos
  for (const port of FALLBACK_PORTS) {
    const alternativeUrl = `http://localhost:${port}/api`;
    try {
      console.log(`🔍 Intentando conectar a: ${alternativeUrl}/test`);
      const r = await axios.get(`${alternativeUrl}/test`, { timeout: 5000 });
      if (r.status === 200) {
        api.defaults.baseURL = alternativeUrl;
        console.log(`✅ Backend conectado correctamente en puerto: ${port}`);
        console.log(`✅ Usando API URL: ${alternativeUrl}`);
        return { success: true, baseURL: alternativeUrl };
      }
    } catch (err) {
      console.log(`❌ No se pudo conectar a puerto ${port}: ${err.message}`);
      // sigue probando con el siguiente puerto
    }
  }

  console.error('❌ No se pudo conectar a ningún puerto del backend.');
  console.error('⚠️ Por favor verifica que:');
  console.error('  1. El servidor backend esté iniciado (npm run dev en /backend)');
  console.error('  2. MySQL esté funcionando y las credenciales sean correctas');
  console.error('  3. El firewall no esté bloqueando los puertos');
  return { success: false };
};

// Compat wrapper para mantener backward-compatibility con llamadas existentes
export const testBackendConnection = async () => {
  const res = await initApi();
  if (res && res.success) {
    // devolver similar a la forma anterior: { success: true, port: API_BASE_URL }
    return { success: true, port: res.baseURL || api.defaults.baseURL };
  }
  return { success: false, error: 'No se pudo conectar a ningún puerto del backend' };
};

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Auto-reintentar en caso de 503 (servidor caído temporalmente)
    if (error.response?.status === 503) {
      const maxRetries = 3;
      const retryDelay = 3000; // 3 segundos
      
      // Inicializar contador si no existe
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }
      
      if (originalRequest._retryCount < maxRetries) {
        originalRequest._retryCount += 1;
        
        console.warn(`⚠️ Error 503 - Reintento ${originalRequest._retryCount}/${maxRetries} en ${retryDelay/1000}s...`);
        
        // Esperar antes de reintentar
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        
        // Reintentar la petición original
        return api(originalRequest);
      } else {
        console.error('❌ Servidor no disponible después de', maxRetries, 'reintentos. Espera 1-2 minutos y recarga la página.');
      }
    }
    
    if (error.response?.status === 401) {
        // Token expirado o inválido
        // TEMPORARY DEBUG: suppress automatic redirect to /login so we can capture
        // console/network traces and identify the root cause of reload loops.
        // Do NOT keep this in production — restore redirect behavior after debugging.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        console.warn('Intercepted 401 — redirect suppressed for debugging.');
        // window.location.href = '/login';
      }
    
    // Mejorar el logging de errores
    if (error.response) {
      console.error('Error de respuesta del servidor:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config.url,
        method: error.config.method
      });
    } else if (error.request) {
      console.error('Error de solicitud (no se recibió respuesta):', error.request);
    } else {
      console.error('Error al configurar la solicitud:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default api;
